import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { HTMLEditor } from '@/components/common/html-editor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { jobsApi } from '@/services/jobs'
import crewsApi from '@/services/crews'
import { categoriesApi } from '@/services/categories'
import { locationsApi } from '@/services/locations'
import { workersApi } from '@/services/workers'
import type { StoreOrderSummary } from '@/types'
import { getErrorMessage } from '@/lib/api'
import { getMapSearchCity } from '@/lib/map-location'
import { getServiceInstanceIdFromSearch, resolveEventScopedServiceInstanceId, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels, WorkspacePortalProvider } from '@/contexts/workspace-portal-context'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { scheduledHoursFromSchedule } from '@/lib/job-schedule-form'
import { isEnglishFriendlyTitle, slugifyPublicPath } from '@/lib/public-slug'
import { cn } from '@/lib/utils'
import { OrderSelector } from '@/components/orders/order-selector'
import { JobScheduleFields, bindJobScheduleFormFields } from '@/components/jobs/job-schedule-fields'
import { JobAcceptanceModeFields, bindJobAcceptanceModeFormFields } from '@/components/jobs/job-acceptance-mode-fields'
import {
  JobMediaGalleryEditor,
  releasePendingJobMediaFiles,
  uploadPendingJobMedia,
  type PendingJobMediaFile,
} from '@/components/jobs/job-media-gallery-editor'
import { useAuthStore } from '@/stores/auth-store'
import eventTagsApi from '@/services/event-tags'
import { EventTagsSelect } from '@/components/events/event-tags-select'
import { JobCreateFromStatementCard } from '@/components/jobs/job-create-from-statement-card'
import type { AiAssistParseResult } from '@/services/ai-assist'

const jobSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  job_code: z.string().optional(),
  job_landmark: z.string().optional(),
  order_reference_id: z.string().optional(),
  recurrence_rule: z.enum(['none', 'daily', 'weekly', 'bi_weekly', 'monthly', 'every_n_days', 'weekly_days', 'monthly_day']).default('none'),
  recurrence_interval_days: z.string().optional(),
  recurrence_weekdays: z.array(z.number().int().min(0).max(6)).default([]),
  recurrence_month_day: z.string().optional(),
  recurrence_count: z.string().optional(),
  recurrence_end: z.string().optional(),
  category: z.string().trim().min(1, 'Category is required'),
  tag_ids: z.array(z.string()).default([]),
  primary_worker: z.string().optional(),
  team_template: z.string().optional(),
  staffed_team: z.string().optional(),
  client_user: z.string().optional(),
  client_name: z.string().trim().optional(),
  client_contact: z.string().optional(),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  phone: z.string().trim().optional(),
  location_id: z.string().optional(),
  location: z.string().optional(),
  city: z.string().optional(),
  address: z.string().optional(),
  status: z.string().default('draft'),
  visibility: z.enum(['unset', 'public', 'private']).default('unset'),
  priority: z.string().default('medium'),
  start_date: z.string().optional(),
  start_time: z.string().optional(),
  end_date: z.string().optional(),
  end_time: z.string().optional(),
  processing_time: z.string().optional(),
  processing_unit: z.enum(['min', 'hr', 'day']).default('hr'),
  estimated_hours: z.coerce.number().optional(),
  budget: z.coerce.number().optional(),
  assignment_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_acceptance_mode: z.enum(['manual', 'auto']).default('manual'),
  workday_entry_approval_mode: z.enum(['manual', 'auto']).default('manual'),
}).superRefine((data, ctx) => {
  if (data.recurrence_rule === 'every_n_days') {
    const interval = Number(data.recurrence_interval_days)
    if (!Number.isFinite(interval) || interval <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the number of days between recurring jobs.',
        path: ['recurrence_interval_days'],
      })
    }
  }
  if (data.recurrence_rule === 'weekly_days' && data.recurrence_weekdays.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Select at least one weekday.',
      path: ['recurrence_weekdays'],
    })
  }
  if (data.recurrence_rule === 'monthly_day') {
    const day = Number(data.recurrence_month_day)
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a day of month between 1 and 31.',
        path: ['recurrence_month_day'],
      })
    }
  }
  if (data.recurrence_rule !== 'none' && data.recurrence_count) {
    const count = Number(data.recurrence_count)
    if (!Number.isInteger(count) || count <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Occurrences must be a positive number.',
        path: ['recurrence_count'],
      })
    }
  }
})

type JobFormData = z.infer<typeof jobSchema>

const splitIsoDateTime = (value?: string | null): { date: string; time: string } => {
  const raw = String(value || '').trim()
  if (!raw) {
    return { date: '', time: '' }
  }

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const [datePart, timePart = ''] = normalized.split('T')
  const time = timePart.slice(0, 5)
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '',
    time: /^\d{2}:\d{2}$/.test(time) ? time : '',
  }
}

const JOB_FORM_PRIORITIES = new Set(['low', 'medium', 'high', 'urgent', 'critical'])
const JOB_FORM_STATUSES = new Set(['draft', 'scheduled', 'in_progress', 'completed', 'cancelled', 'on_hold'])

export function EventCreatePageContent(){
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const labels = useWorkspaceLabels()
  const isEventMode = labels.mode === 'event'
  const isProjectMode = labels.mode === 'project'
  const isJobMode = labels.mode === 'job'
  const serviceInstanceId = useMemo(() => {
    if (isEventMode) {
      return resolveEventScopedServiceInstanceId({
        search: location.search,
        post: currentPost,
        availablePosts,
        storedServiceInstanceId,
      })
    }
    return getServiceInstanceIdFromSearch(location.search) || storedServiceInstanceId
  }, [availablePosts, currentPost, isEventMode, location.search, storedServiceInstanceId])
  const requestedKind = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const kind = params.get('kind')?.trim().toLowerCase()
    return kind === 'committee' || kind === 'task' ? kind : null
  }, [location.search])
  const isCommitteeCreate = requestedKind === 'committee'
  const isTaskCreate = requestedKind === 'task'
  const entityLabel = labels.entity
  const createEntityLabel = isCommitteeCreate ? 'Committee' : isTaskCreate ? 'Task' : entityLabel
  const entityListPath = labels.basePath
  const parentEventId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('parent_event_id')?.trim() || ''
  }, [location.search])
  const isSubEventCreate = isEventMode && Boolean(parentEventId)
  const categoryKind = isEventMode ? 'event' : isProjectMode ? 'project' : 'job'
  const [selectedOrder, setSelectedOrder] = useState<StoreOrderSummary | null>(null)
  const [prefillApplied, setPrefillApplied] = useState(false)
  const { data: categoriesResponse, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['job-categories', categoryKind],
    queryFn: () => categoriesApi.getCategories({
      kind: categoryKind,
    }),
  })
  const { data: jobManagementConfig } = useQuery({
    queryKey: ['job-management-configuration'],
    queryFn: jobsApi.getManagementConfiguration,
    enabled: Boolean(serviceInstanceId),
  })
  const { data: eventTags = [], isLoading: isEventTagsLoading } = useQuery({
    queryKey: ['event-tags', serviceInstanceId ?? 'none'],
    queryFn: () => eventTagsApi.list(),
    enabled: isEventMode && Boolean(serviceInstanceId),
  })
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      order_reference_id: '',
      recurrence_rule: 'none',
      recurrence_interval_days: '',
      recurrence_weekdays: [],
      recurrence_month_day: '',
      recurrence_count: '',
      recurrence_end: '',
      category: '',
      tag_ids: [],
      primary_worker: 'unassigned',
      team_template: 'none',
      staffed_team: 'none',
      client_user: 'none',
      location_id: 'none',
      status: 'draft',
      visibility: 'unset',
      priority: 'medium',
      start_time: '09:00',
      processing_unit: 'hr',
      assignment_acceptance_mode: 'manual',
      workday_entry_acceptance_mode: 'manual',
      workday_entry_approval_mode: 'manual',
    },
  })
  const selectedCategory = watch('category')
  const selectedPrimaryWorker = watch('primary_worker')
  const selectedTeamTemplate = watch('team_template')
  const selectedStaffedTeam = watch('staffed_team')
  const selectedLocationId = watch('location_id')
  const recurrenceRule = watch('recurrence_rule')
  const isRecurrentBookingEnabled = jobManagementConfig?.enable_recurrent_job_booking !== false

  useEffect(() => {
    if (prefillApplied) {
      return
    }

    const params = new URLSearchParams(location.search)
    const title = params.get('title')?.trim()
    const description = params.get('description')?.trim()
    const startDate = params.get('start_date')?.trim()
    const startTime = params.get('start_time')?.trim()
    const endDate = params.get('end_date')?.trim()
    const endTime = params.get('end_time')?.trim()
    const category = params.get('category')?.trim()

    const hasPrefill = Boolean(title || description || startDate || startTime || endDate || endTime || category)
    if (!hasPrefill) {
      setPrefillApplied(true)
      return
    }

    const options = { shouldDirty: false, shouldValidate: false } as const
    if (title) {
      setValue('title', title, options)
    }
    if (description) {
      setValue('description', description, options)
    }
    if (startDate) {
      setValue('start_date', startDate, options)
    }
    if (startTime) {
      setValue('start_time', startTime, options)
    }
    if (endDate) {
      setValue('end_date', endDate, options)
    }
    if (endTime) {
      setValue('end_time', endTime, options)
    }
    if (category) {
      setValue('category', category, options)
    }

    setPrefillApplied(true)
  }, [location.search, prefillApplied, setValue])

  const { data: workersResponse, isLoading: isWorkersLoading } = useQuery({
    queryKey: ['job-primary-workers', selectedCategory],
    queryFn: () => workersApi.getWorkers({ page_size: 200, active: true, category: selectedCategory }),
    enabled: Boolean(selectedCategory),
  })
  const { data: teamTemplates = [], isLoading: isTeamTemplatesLoading } = useQuery({
    queryKey: ['job-team-templates', serviceInstanceId ?? 'none'],
    queryFn: () => crewsApi.listTemplates({
      service_instance_id: serviceInstanceId ?? undefined,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })
  const { data: staffedTeams = [], isLoading: isStaffedTeamsLoading } = useQuery({
    queryKey: ['job-staffed-teams', serviceInstanceId ?? 'none', selectedTeamTemplate ?? 'none'],
    queryFn: () => crewsApi.listCrews({
      service_instance_id: serviceInstanceId ?? undefined,
      crew_template_id: selectedTeamTemplate !== 'none' ? selectedTeamTemplate : undefined,
      ready: true,
      active: true,
      page_size: 200,
    }),
    enabled: Boolean(serviceInstanceId && selectedTeamTemplate && selectedTeamTemplate !== 'none'),
    staleTime: 30_000,
  })
  const { data: locationsResponse = [], isLoading: isLocationsLoading } = useQuery({
    queryKey: ['job-locations', serviceInstanceId ?? 'none'],
    queryFn: () => locationsApi.list({
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })
  const categories = categoriesResponse?.results ?? []
  const workers = workersResponse?.results ?? []
  const locations = locationsResponse

  const primaryWorkerOptions = useMemo(
    () => [
      { value: 'unassigned', label: 'No primary worker', keywords: 'unassigned none' },
      ...workers.map((worker) => ({
        value: String(worker.id),
        label: getUserDropdownLabel(worker, worker.id),
        keywords: getUserDropdownKeywords(worker),
      })),
    ],
    [workers],
  )
  const teamTemplateOptions = useMemo(
    () => [
      { value: 'none', label: 'No team template', keywords: 'none unassigned' },
      ...teamTemplates.map((template) => ({
        value: String(template.id),
        label: template.name,
        keywords: `${template.name ?? ''} ${template.code ?? ''}`.trim(),
      })),
    ],
    [teamTemplates],
  )
  const staffedTeamOptions = useMemo(
    () => [
      { value: 'none', label: 'No staffed team', keywords: 'none unassigned' },
      ...staffedTeams.map((team) => ({
        value: String(team.id),
        label: team.name,
        keywords: `${team.name ?? ''} ${team.code ?? ''}`.trim(),
      })),
    ],
    [staffedTeams],
  )

  useEffect(() => {
    if (!selectedCategory && selectedPrimaryWorker !== 'unassigned') {
      setValue('primary_worker', 'unassigned')
      return
    }

    if (
      !isWorkersLoading &&
      selectedPrimaryWorker &&
      selectedPrimaryWorker !== 'unassigned' &&
      !workers.some((worker) => String(worker.id) === selectedPrimaryWorker)
    ) {
      setValue('primary_worker', 'unassigned')
    }
  }, [isWorkersLoading, selectedCategory, selectedPrimaryWorker, setValue, workers])

  useEffect(() => {
    if (!selectedTeamTemplate || selectedTeamTemplate === 'none') {
      if (selectedStaffedTeam && selectedStaffedTeam !== 'none') {
        setValue('staffed_team', 'none')
      }
      return
    }

    if (
      !isStaffedTeamsLoading
      && selectedStaffedTeam
      && selectedStaffedTeam !== 'none'
      && !staffedTeams.some((team) => String(team.id) === selectedStaffedTeam)
    ) {
      setValue('staffed_team', 'none')
    }
  }, [isStaffedTeamsLoading, selectedStaffedTeam, selectedTeamTemplate, setValue, staffedTeams])

  useEffect(() => {
    if (!selectedLocationId || selectedLocationId === 'none') {
      return
    }

    const selectedLocation = locations.find((item) => String(item.id) === selectedLocationId)
    if (!selectedLocation) {
      if (!isLocationsLoading) {
        setValue('location_id', 'none', { shouldDirty: true })
      }
      return
    }

    setValue('location', selectedLocation.place, { shouldDirty: true })
    setValue('city', getMapSearchCity({ label: selectedLocation.place }), { shouldDirty: true })
  }, [isLocationsLoading, locations, selectedLocationId, setValue])

  const [pendingMedia, setPendingMedia] = useState<PendingJobMediaFile[]>([])

  useEffect(() => () => releasePendingJobMediaFiles(pendingMedia), [pendingMedia])

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof jobsApi.create>[0]) => (
      isCommitteeCreate
        ? jobsApi.createCommittee(payload)
        : isTaskCreate
          ? jobsApi.createTask(payload)
          : isEventMode
            ? jobsApi.createEvent(payload)
            : isProjectMode
              ? jobsApi.createProject(payload)
              : jobsApi.create(payload)
    ),
    onSuccess: async (data) => {
      const hadPendingMedia = pendingMedia.length > 0
      try {
        if (hadPendingMedia) {
          await uploadPendingJobMedia(data.id, pendingMedia)
          releasePendingJobMediaFiles(pendingMedia)
          setPendingMedia([])
        }
        toast.success(hadPendingMedia ? `${entityLabel} and gallery files created successfully` : `${entityLabel} created successfully`)
      } catch (error) {
        toast.error(getErrorMessage(error))
        toast.message(`${entityLabel} was created, but some gallery uploads failed. Open the record to retry.`)
      }
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      if (isSubEventCreate && parentEventId) {
        queryClient.invalidateQueries({ queryKey: ['sub-events', parentEventId] })
      }
      navigate(withServiceInstanceId(`${entityListPath}/${data.id}`, serviceInstanceId))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const onSubmit = (data: JobFormData) => {
    const estimatedHours = scheduledHoursFromSchedule(
      data.start_date,
      data.start_time,
      data.end_date,
      data.end_time,
      data.processing_time,
      data.processing_unit,
    )

    createMutation.mutate({
      ...data,
      service_instance_id: serviceInstanceId || undefined,
      // Slug field is hidden on create; auto-set from English title for public URLs.
      // The public path still appends the new id: `/venue/events/:id/:slug`.
      job_code: isEventMode
        ? (isEnglishFriendlyTitle(data.title) ? slugifyPublicPath(data.title) : null)
        : (data.job_code?.trim() || null),
      job_landmark: data.job_landmark?.trim() || null,
      parent_event_id: isSubEventCreate ? parentEventId : null,
      estimated_hours: estimatedHours,
      recurrence_interval_days: data.recurrence_rule === 'every_n_days' && data.recurrence_interval_days
        ? Number(data.recurrence_interval_days)
        : null,
      recurrence_weekdays: data.recurrence_rule === 'weekly_days' ? data.recurrence_weekdays : [],
      recurrence_month_day: data.recurrence_rule === 'monthly_day' && data.recurrence_month_day
        ? Number(data.recurrence_month_day)
        : null,
      recurrence_count: data.recurrence_rule !== 'none' && data.recurrence_count
        ? Number(data.recurrence_count)
        : null,
      recurrence_end: data.recurrence_rule !== 'none' && data.recurrence_end ? data.recurrence_end : null,
      order_reference_id: data.order_reference_id?.trim() || null,
      primary_worker: data.primary_worker && data.primary_worker !== 'unassigned' ? data.primary_worker : undefined,
      team_template: data.team_template && data.team_template !== 'none' ? data.team_template : null,
      staffed_team: data.staffed_team && data.staffed_team !== 'none' ? data.staffed_team : null,
      client_user: data.client_user && data.client_user !== 'none' ? data.client_user : null,
      location_id: data.location_id && data.location_id !== 'none' ? data.location_id : null,
      tag_ids: isEventMode ? data.tag_ids.map((tagId) => Number(tagId)).filter((tagId) => Number.isFinite(tagId)) : undefined,
      visibility: isEventMode
        ? (data.visibility === 'public' || data.visibility === 'private' ? data.visibility : null)
        : undefined,
    })
  }

  const status = watch('status')
  const visibility = watch('visibility')
  const priority = watch('priority')

  const applyAiDraft = (parsed: AiAssistParseResult) => {
    const draft = parsed.draft || {}
    const options = { shouldDirty: true, shouldValidate: true } as const

    if (draft.title) {
      setValue('title', String(draft.title), options)
    }
    if (draft.description != null) {
      setValue('description', String(draft.description), options)
    }
    if (draft.category != null && String(draft.category).trim()) {
      setValue('category', String(draft.category), options)
    }

    const clientName = String(draft.customer_name || draft.client || '').trim()
    if (clientName) {
      setValue('client_name', clientName, options)
    }
    if (draft.email != null) {
      setValue('email', String(draft.email), options)
    }
    if (draft.phone != null) {
      setValue('phone', String(draft.phone), options)
    }
    if (draft.first_line != null) {
      setValue('address', String(draft.first_line), options)
    }
    if (draft.city != null) {
      setValue('city', String(draft.city), options)
    }
    if (draft.job_landmark != null) {
      setValue('job_landmark', String(draft.job_landmark), options)
    }

    const priorityValue = String(draft.priority || '').trim().toLowerCase()
    if (JOB_FORM_PRIORITIES.has(priorityValue)) {
      setValue('priority', priorityValue, options)
    }

    const statusValue = String(draft.status || '').trim().toLowerCase()
    if (JOB_FORM_STATUSES.has(statusValue)) {
      setValue('status', statusValue, options)
    }

    const start = splitIsoDateTime(
      typeof draft.scheduled_start === 'string' ? draft.scheduled_start : null,
    )
    if (start.date) {
      setValue('start_date', start.date, options)
    }
    if (start.time) {
      setValue('start_time', start.time, options)
    }

    const end = splitIsoDateTime(
      typeof draft.scheduled_end === 'string' ? draft.scheduled_end : null,
    )
    if (end.date) {
      setValue('end_date', end.date, options)
    }
    if (end.time) {
      setValue('end_time', end.time, options)
    }

    if (draft.scheduled_hours != null && draft.scheduled_hours !== '') {
      const hours = Number(draft.scheduled_hours)
      if (Number.isFinite(hours)) {
        setValue('estimated_hours', hours, options)
      }
    }

    if (draft.job_pay != null && draft.job_pay !== '') {
      const budget = Number(draft.job_pay)
      if (Number.isFinite(budget)) {
        setValue('budget', budget, options)
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(withServiceInstanceId(entityListPath, serviceInstanceId))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isSubEventCreate && !isCommitteeCreate && !isTaskCreate ? 'Add Sub Event' : `Create ${createEntityLabel}`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isSubEventCreate && !isCommitteeCreate && !isTaskCreate
              ? `Create a child event under ${entityLabel.toLowerCase()} #${parentEventId}.`
              : `Add a new ${createEntityLabel.toLowerCase()} to the system`}
          </p>
        </div>
      </div>

      {isJobMode ? (
        <JobCreateFromStatementCard
          serviceInstanceId={serviceInstanceId}
          disabled={createMutation.isPending}
          onApplyDraft={applyAiDraft}
        />
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
              <CardDescription>{`Enter the ${entityLabel.toLowerCase()} details`}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">{`${entityLabel} Title *`}</Label>
                <Input
                  id="title"
                  placeholder={`Enter ${entityLabel.toLowerCase()} title`}
                  {...register('title')}
                  aria-invalid={!!errors.title}
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className={cn('gap-4', isEventMode ? 'space-y-2' : 'grid sm:grid-cols-2')}>
                {!isEventMode ? (
                  <div className="space-y-2">
                    <Label htmlFor="job_code">Code</Label>
                    <Input
                      id="job_code"
                      placeholder="Optional business code"
                      {...register('job_code')}
                    />
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label htmlFor="job_landmark">Landmark</Label>
                  <Input
                    id="job_landmark"
                    placeholder="Optional landmark or venue"
                    {...register('job_landmark')}
                  />
                </div>
              </div>

              <div className={cn('gap-4', isEventMode ? 'grid sm:grid-cols-2' : 'space-y-2')}>
                <div className="space-y-2">
                  <Label htmlFor="category">{isEventMode ? 'Event Category *' : 'Category *'}</Label>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange} disabled={isCategoriesLoading || categories.length === 0}>
                        <SelectTrigger id="category" aria-invalid={!!errors.category}>
                          <SelectValue placeholder={isCategoriesLoading ? 'Loading categories...' : 'Select category'} />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.category && (
                    <p className="text-sm text-destructive">{errors.category.message}</p>
                  )}
                  {!isCategoriesLoading && categories.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {isProjectMode
                        ? 'Create a category first before creating a project.'
                        : isEventMode
                          ? 'Create a category first before creating an event.'
                          : 'Create a category first before creating a job.'}
                    </p>
                  )}
                </div>

                {isEventMode ? (
                  <div className="space-y-2">
                    <Label htmlFor="event-tags">Tags</Label>
                    <Controller
                      control={control}
                      name="tag_ids"
                      render={({ field }) => (
                        <EventTagsSelect
                          id="event-tags"
                          value={field.value ?? []}
                          onValueChange={field.onChange}
                          tags={eventTags}
                          disabled={isEventTagsLoading || !serviceInstanceId}
                          placeholder={isEventTagsLoading ? 'Loading tags...' : 'Select tags'}
                        />
                      )}
                    />
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <HTMLEditor
                      id="description"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter job description (HTML supported)"
                      rows={4}
                    />
                  )}
                />
              </div>

              {isJobMode ? (
                <OrderSelector
                  value={watch('order_reference_id')}
                  selectedOrder={selectedOrder}
                  onSelect={(order) => {
                    setSelectedOrder(order)
                    setValue('order_reference_id', order?.reference_id ?? '', { shouldDirty: true, shouldValidate: true })
                  }}
                  error={errors.order_reference_id?.message}
                />
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="primary_worker">Incharge</Label>
                <Controller
                  control={control}
                  name="primary_worker"
                  render={({ field }) => (
                    <SearchableSelect
                      id="primary_worker"
                      ariaLabel="Primary worker"
                      value={field.value || 'unassigned'}
                      onValueChange={field.onChange}
                      options={primaryWorkerOptions}
                      placeholder={!selectedCategory ? 'Select category first' : isWorkersLoading ? 'Loading workers...' : 'Select primary worker'}
                      searchPlaceholder="Search workers"
                      emptyText="No workers found."
                      disabled={!selectedCategory || isWorkersLoading}
                    />
                  )}
                />
                {selectedCategory && !isWorkersLoading && workers.length === 0 && (
                  <p className="text-sm text-muted-foreground">No workers are mapped to the selected category.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="team_template">Team Template</Label>
                <Controller
                  control={control}
                  name="team_template"
                  render={({ field }) => (
                    <SearchableSelect
                      id="team_template"
                      ariaLabel="Team template"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={teamTemplateOptions}
                      placeholder={isTeamTemplatesLoading ? 'Loading team templates...' : 'Select team template'}
                      searchPlaceholder="Search team templates"
                      emptyText="No team templates found."
                      disabled={!serviceInstanceId || isTeamTemplatesLoading}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="staffed_team">Staffed Team</Label>
                <Controller
                  control={control}
                  name="staffed_team"
                  render={({ field }) => (
                    <SearchableSelect
                      id="staffed_team"
                      ariaLabel="Staffed team"
                      value={field.value || 'none'}
                      onValueChange={field.onChange}
                      options={staffedTeamOptions}
                      placeholder={selectedTeamTemplate === 'none' ? 'Select team template first' : isStaffedTeamsLoading ? 'Loading staffed teams...' : 'Select staffed team'}
                      searchPlaceholder="Search staffed teams"
                      emptyText="No staffed teams found."
                      disabled={!serviceInstanceId || selectedTeamTemplate === 'none' || isStaffedTeamsLoading}
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) => setValue('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={priority}
                    onValueChange={(value) => setValue('priority', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isEventMode ? (
                <div className="space-y-2">
                  <Label>Visibility</Label>
                  <Select
                    value={visibility}
                    onValueChange={(value: 'unset' | 'public' | 'private') => setValue('visibility', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Unset (hidden from public site)</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only public events appear on the venue website.
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>



          {/* Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Schedule</CardTitle>
              <CardDescription>Set job timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <JobScheduleFields {...bindJobScheduleFormFields({ control, register, setValue, errors })} />

              {isRecurrentBookingEnabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_rule">Recurrence</Label>
                    <Controller
                      control={control}
                      name="recurrence_rule"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="recurrence_rule">
                            <SelectValue placeholder="Select recurrence" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Does not recur</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="every_n_days">After specific number of days</SelectItem>
                            <SelectItem value="weekly_days">Weekly on selected days</SelectItem>
                            <SelectItem value="monthly_day">Monthly on a specific day</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  {recurrenceRule === 'every_n_days' && (
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_interval_days">Repeat every N days</Label>
                      <Input
                        id="recurrence_interval_days"
                        type="number"
                        min="1"
                        placeholder="Enter days"
                        {...register('recurrence_interval_days')}
                      />
                      {errors.recurrence_interval_days && (
                        <p className="text-sm text-destructive">{errors.recurrence_interval_days.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule === 'weekly_days' && (
                    <div className="space-y-2">
                      <Label>Repeat on these days</Label>
                      <Controller
                        control={control}
                        name="recurrence_weekdays"
                        render={({ field }) => (
                          <div className="flex flex-wrap gap-2">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, dayIndex) => {
                              const isSelected = field.value.includes(dayIndex)
                              return (
                                <Button
                                  key={label}
                                  type="button"
                                  size="sm"
                                  variant={isSelected ? 'default' : 'outline'}
                                  onClick={() =>
                                    field.onChange(
                                      isSelected
                                        ? field.value.filter((day) => day !== dayIndex)
                                        : [...field.value, dayIndex].sort((a, b) => a - b)
                                    )
                                  }
                                >
                                  {label}
                                </Button>
                              )
                            })}
                          </div>
                        )}
                      />
                      {errors.recurrence_weekdays && (
                        <p className="text-sm text-destructive">{errors.recurrence_weekdays.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule === 'monthly_day' && (
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_month_day">Day of month</Label>
                      <Input
                        id="recurrence_month_day"
                        type="number"
                        min="1"
                        max="31"
                        placeholder="e.g. 15"
                        {...register('recurrence_month_day')}
                      />
                      {errors.recurrence_month_day && (
                        <p className="text-sm text-destructive">{errors.recurrence_month_day.message}</p>
                      )}
                    </div>
                  )}

                  {recurrenceRule !== 'none' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_end">Repeat until</Label>
                        <Input
                          id="recurrence_end"
                          type="date"
                          {...register('recurrence_end')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_count">Number of occurrences (optional)</Label>
                        <Input
                          id="recurrence_count"
                          type="number"
                          min="1"
                          placeholder="Leave blank for open-ended"
                          {...register('recurrence_count')}
                        />
                        {errors.recurrence_count && (
                          <p className="text-sm text-destructive">{errors.recurrence_count.message}</p>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Budget */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Budget</CardTitle>
              <CardDescription>Set job budget</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Amount</Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  {...register('budget')}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {isJobMode ? (
          <JobAcceptanceModeFields {...bindJobAcceptanceModeFormFields({ control })} />
        ) : null}

        <JobMediaGalleryEditor
          pendingFiles={pendingMedia}
          onPendingFilesChange={setPendingMedia}
          title={labels.galleryTitle}
          description={isProjectMode
            ? 'Add images and videos now. They upload automatically after the project is created.'
            : 'Add images and videos now. They upload automatically after the job is created.'}
        />

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(withServiceInstanceId(entityListPath, serviceInstanceId))}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending || isCategoriesLoading || categories.length === 0}>
            {createMutation.isPending && <Spinner className="mr-2" />}
            {`Create ${entityLabel}`}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default function EventCreatePage() {
  return (
    <WorkspacePortalProvider mode="event">
      <EventCreatePageContent />
    </WorkspacePortalProvider>
  )
}
